/**
 * Leaving the SPA, in one function — and the one way to build an absolute URL on
 * this origin.
 *
 * `integrations/clock.ts`'s neighbour and for the same reason: a browser global that
 * a test cannot move is a rule nothing can check. `location.assign` in jsdom is a
 * **non-configurable** property, so `vi.spyOn(location, 'assign')` throws "Cannot
 * redefine property" — which means every hard navigation in this app was untestable
 * from the outside, and three of them were written independently.
 *
 * ## Why a hard navigation exists at all
 *
 * Three places, and all three are the same argument: the identity or the money on the
 * wire is about to change, so **the cache is wrong**.
 *
 *   - `ImpersonateButton` — the cookie becomes the client's, and every studio answer
 *     React Query holds was given to staff. A router navigation would render the
 *     studio's own numbers under the impersonation banner.
 *   - **`auth/actions.ts` `stopImpersonating`** — the same argument in reverse, and
 *     session 24 (R3-24) is what made it one. Ending an impersonation invalidated the
 *     whole cache and *then* navigated, so every mounted client query refetched with
 *     a staff cookie: six `/api/clients/me/*` requests, all 403, before the router
 *     moved. Nothing broke, and a routine action filling the console with
 *     authorisation failures is exactly the noise that hides a real one later. A full
 *     load has no cache to refetch.
 *   - `PrepaidSection` and `PaymentMethodPanel` — the reader is being sent to Stripe.
 *     That is another origin; there is no router route for it.
 *
 * Anything else is `<Link>`. A hard load throws away the router's history, the query
 * cache and the scroll position, and on a portal page that is a second of blank.
 *
 * ## `absoluteUrl`
 *
 * The other half of "this origin", and the same duplication: `routes/login.tsx` builds
 * an absolute callback for a magic link (a relative `callbackURL` resolves against
 * **Better Auth's** base, not the SPA's — `CLAUDE.md` §Gotchas), and the studio money
 * page builds one for a chase mail that names an invoice. Both are `new URL(path,
 * origin)`, and both are a link somebody outside this tab will click.
 *
 * ## `currentSearch`
 *
 * The third global, and it is here for a sharper reason than the other two: there is
 * a plausible-looking substitute that is wrong. `router.state.location.searchStr` is
 * **not** the query the browser was given — TanStack Router re-stringifies it from
 * what `parseSearch` decoded, and `qss` `encode` writes each key once, so a repeated
 * parameter comes back collapsed. `auth/oauth.ts` says why that matters: the OAuth
 * provider signs the whole query and repeats `ba_param` once per signed name, so the
 * router's copy of a consent URL fails verification every time. The address bar is
 * the only place the real query still exists.
 *
 * Being a function is also the only way a test can say what it is. `renderApp` drives
 * the real tree over a **memory** history, so jsdom's `location.search` is empty
 * however the test spelled the route, and `location` is non-configurable —
 * `vi.spyOn(navigate, 'currentSearch')` is what a routing test does instead.
 */

/** Where the client portal opens. Named, because two of the callers use it. */
export const CLIENT_PORTAL = '/app'

/** Where the studio portal opens, and where an impersonation ends. */
export const STUDIO_PORTAL = '/studio'

/**
 * Leave the SPA for `url` — a full document load.
 *
 * Takes the whole URL, absolute or same-origin, because one of the three callers is
 * handed one by Stripe.
 */
export function leaveTheSpa(url: string): void {
  globalThis.location.assign(url)
}

/** `/app/billing/FES-2026-047` on this origin, as a string somebody can be sent. */
export function absoluteUrl(path: string): string {
  return new URL(path, globalThis.location.origin).toString()
}

/** The address bar's query string, verbatim — `?ba_param=a&ba_param=b` and all. */
export function currentSearch(): string {
  return globalThis.location.search
}
